import React, { Fragment } from 'react'

import {
  IconCaretDown,
  IconChevronLeft,
  IconChevronRight,
  IconDots,
} from '@tabler/icons-react'
import { Menu } from 'antd'
import { range, startCase } from 'lodash'
import styled from 'styled-components'

import { Button } from '../shared-components/BUI/Button'
import { Dropdown } from '../shared-components/BUI/Dropdown'

import { pluraliseWord } from '@/utils/pluralise'

import { buildPassTheme } from 'src/styles/theme'

const Wrapper = styled.div`
  margin-top: 0.5rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;

  & > svg {
    color: ${({ theme }) => theme.colour.secondary._400};
  }
`

type PaginationProps = {
  currentPage: number
  totalPages: number
  pageSize: number
  onPageClick: (pageNumber: number) => void
  onPageSizeClick: (pageSize: number) => void
  resourceSingular: string
  pageSizeOptions?: number[]
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  pageSize,
  onPageClick,
  onPageSizeClick,
  resourceSingular,
  pageSizeOptions = [20, 50, 100, 150],
}) => {
  const truncateStart = currentPage > 3
  const truncateEnd = totalPages + 1 - currentPage > 3

  const chunks = []
  if (!truncateStart && !truncateEnd) {
    chunks.push(range(1, totalPages + 1))
  } else if (truncateStart && !truncateEnd) {
    chunks.push([1], range(totalPages - 3, totalPages + 1))
  } else if (!truncateStart && truncateEnd) {
    chunks.push(range(1, 5), [totalPages])
  } else if (truncateStart && truncateEnd) {
    chunks.push([1], range(currentPage - 1, currentPage + 2), [totalPages])
  }

  const items = [
    {
      key: 'title',
      type: 'group',
      label: `${startCase(pluraliseWord(2, resourceSingular))} per page`,
    },
    ...pageSizeOptions.map((size) => ({
      key: size.toString(),
      label: size.toString(),
      onClick: () => onPageSizeClick(size),
    })),
  ]

  return (
    <Wrapper>
      <Dropdown trigger={['click']} overlay={<Menu items={items} />}>
        <Button
          icon={
            <IconCaretDown
              size={16}
              fill={buildPassTheme.colour.secondary.base}
            />
          }
          iconPlacement="right"
        >
          {pageSize}
        </Button>
      </Dropdown>

      {currentPage > 1 && (
        <Button
          variant="secondary-text"
          onClick={() => onPageClick(currentPage - 1)}
          icon={<IconChevronLeft size={16} />}
        />
      )}

      {chunks.map((chunk, index) => (
        <Fragment key={`${chunk}-${index}`}>
          {chunk.map((page) => (
            <Button
              key={page}
              naked
              variant={page === currentPage ? 'primary' : 'secondary'}
              onClick={() => onPageClick(page)}
            >
              {page}
            </Button>
          ))}
          {index < chunks.length - 1 && <IconDots size={16} />}
        </Fragment>
      ))}

      <Button
        variant="secondary-text"
        onClick={() => onPageClick(currentPage + 1)}
        icon={<IconChevronRight size={16} />}
        disabled={currentPage >= totalPages}
      />
    </Wrapper>
  )
}
